import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { 
  Typography, 
  Box
} from "@material-ui/core";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import getAPI from "@API/index";
import { FetchOnceAutocomplete } from "@View/common/form/control/FetchOnceAutocomplete";

const useStyles = makeStyles((theme) => ({
  selectContainer: {
    marginBottom: theme.spacing(3),
  },
  chartContainer: {
    height: "400px",
    marginTop: theme.spacing(2),
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "200px",
  },
  placeholder: {
    textAlign: "center",
    color: "#54606F",
    padding: theme.spacing(4),
    fontSize: "16px",
    lineHeight: "24px",
  },
}));

// Функция поиска кастомеров для автокомплита
const searchCustomers = async () => {
  try {
    const result = await getAPI().request({
      url: "/api/Customer",
    });
    return result.map((customer) => ({ id: customer.id, name: customer.name }));
  } catch (error) {
    console.error("Error searching customers:", error);
    return [];
  }
};

const RequestsStatisticsTab = () => {
  const classes = useStyles();
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [customerData, setCustomerData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Обработчик выбора кастомера из автокомплита
  const handleCustomerSelect = (customer) => {
    if (customer && customer.id) {
      setSelectedCustomer(customer.id);
      loadCustomerData(customer.id);
    } else {
      setSelectedCustomer("");
      setCustomerData(null);
      setChartData([]);
    }
  };

  // Загрузка данных выбранного кастомера
  const loadCustomerData = async (customerId) => {
    try {
      setLoading(true);
      const response = await getAPI().request({
        url: `/api/Customer/${customerId}`,
        params: {
          includeDisabledRequest: true
        }
      });
      if (response) {
        setCustomerData(response);
        processRequestsData(response.finDataRequests || []);
      }
    } catch (error) {
      console.error("Error loading customer data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Обработка данных запросов для графика
  const processRequestsData = (requests) => {
    const monthlyData = {};
    
    requests.forEach(request => {
      if (request.createdAt) {
        const date = new Date(request.createdAt);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const monthName = date.toLocaleDateString('ru-RU', { year: 'numeric', month: 'long' });
        
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = {
            month: monthName,
            requests: 0,
            date: monthKey
          };
        }
        monthlyData[monthKey].requests += 1;
      }
    });

    // Сортировка по дате
    const sortedData = Object.values(monthlyData).sort((a, b) => a.date.localeCompare(b.date));
    setChartData(sortedData);
  };

  return (
    <Box>
      {/* Выбор кастомера с автокомплитом */}
      <Box className={classes.selectContainer}>
        <FetchOnceAutocomplete
          value=""
          id="customer"
          label="Выберите заказчика"
          fetchOptions={searchCustomers}
          onChange={handleCustomerSelect}
        />
      </Box>

      {/* График */}
      {selectedCustomer && chartData.length > 0 ? (
        <Box className={classes.chartContainer}>
          <Typography variant="h6" gutterBottom>
            Статистика запросов по месяцам
          </Typography>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="month" 
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis />
              <Tooltip />
              <Bar dataKey="requests" fill="#5195E6" />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      ) : selectedCustomer && chartData.length === 0 && !loading ? (
        <Typography className={classes.placeholder}>
          Нет данных о запросах для выбранного заказчика
        </Typography>
      ) : !selectedCustomer ? (
        <Typography className={classes.placeholder}>
          Выберите заказчика для отображения статистики
        </Typography>
      ) : null}
    </Box>
  );
};

export default RequestsStatisticsTab;